import { getErrorMessage } from "../utils/errorMessage";
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  candidatesAPI,
  interviewsAPI,
  interviewersAPI,
  jobsAPI,
  aiAPI
} from '../api/client';
import { CANDIDATE_STATUSES, getStatusStyle, getStatusLabel } from '../constants/candidateStatuses';
import { toast } from 'react-toastify';
import LoadingButton from '../components/LoadingButton';
import Skeleton from '../components/Skeleton';
import ConfirmModal from "../components/ConfirmModal";
import { mayPerform } from '../utils/userAccess';
import '../pages/AnimationsAndStyles.css';

/* ================= CONSTANTS ================= */

// const POSITIONS = [
//   'All',
//   'Embedded System',
//   'Embedded Software',
//   'Post Silicon Validation',
//   'VLSI',
//   'Others',
//   'HR',
//   'Business Unit'
// ];

// Positions will be loaded from the jobs API so they reflect current jobs

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const isValidPersonName = (value) => {
  const name = String(value || '').trim();
  if (!name) return false;
  return /^(?=.*[A-Za-z])[A-Za-z][A-Za-z\s.'-]*$/.test(name);
};

const normalizeText = (value) => String(value || '').trim();

function formatWallClockDateTime(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '—' : value.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  const input = String(value || '').trim();
  if (!input) return '—';
  const match = input.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i);
  if (match) {
    const [, year, month, day, hour, minute, second = '00'] = match;
    const d = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
 
/* ================= COMPONENT ================= */

export default function InterviewsList() {
  const navigate = useNavigate();

  // Employee-specific gate for interview mutations (schedule/reschedule/cancel).
  const canManageInterview = mayPerform("interview", "manage");

  /* ================= STATES ================= */

  const [candidates, setCandidates] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('All');
  const [positions, setPositions] = useState(['All']);
  const [currentPage, setCurrentPage] = useState(1);
  const [interviewCurrentPage, setInterviewCurrentPage] = useState(1);
  
  //  Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [interviewSearchTerm, setInterviewSearchTerm] = useState('');

  //  View Mode Tabs
  const [activeView, setActiveView] = useState(() => (canManageInterview ? 'schedule' : 'list'));
  const [interviews, setInterviews] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [interviewMode, setInterviewMode] = useState('schedule');
  const [isScheduling, setIsScheduling] = useState(false);

  const [interviewers, setInterviewers] = useState([]);
  const [showInterviewerModal, setShowInterviewerModal] = useState(false);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedInterviewLink, setGeneratedInterviewLink] = useState('');
  const [generatedUsername, setGeneratedUsername] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [cancellingInterviewId, setCancellingInterviewId] = useState(null);
  const [savingInterviewer, setSavingInterviewer] = useState(false);
  const [interviewToCancel, setInterviewToCancel] = useState(null);
  const [interviewerToDelete, setInterviewerToDelete] = useState(null);
  const [deletingInterviewer, setDeletingInterviewer] = useState(false);
  const [deletingInterviewerId, setDeletingInterviewerId] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleInterview, setRescheduleInterview] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const [newInterviewer, setNewInterviewer] = useState({
    name: '',
    email: '',
    role: '',
    department: ''
  });

  const [form, setForm] = useState({
    scheduled_date: '',
    interview_type: 'Technical',
    mode: 'standard',
    voice_provider: 'livekit',
    interviewer_department: '',
    resume_text: '',
    resume_file_name: '',
    job_description: '',
    primary_skills: '',
    secondary_skills: '',
    interviewers: [
      { interviewer_name: '', interviewer_email: '', interviewer_role: '', interviewer_department: '' }
    ]
  });

  /* ================= LOAD ================= */

  useEffect(() => {
    fetchCandidates();
    fetchInterviewers();
    fetchPositions();
    fetchInterviews();
  }, []);

  const fetchPositions = async () => {
    try {
      const res = await jobsAPI.getAll();
      const list = res.data.map(p => `${p.title} - ${p.client_name}`);
      setPositions(['All', ...Array.from(new Set(list))]);
    } catch (err) {
      console.error('Failed to load positions:', err);
    }
  };

  /* ================= API ================= */

  const fetchCandidates = async () => {
    try {
      const res = await candidatesAPI.getAll();
      setCandidates(res.data);
      setFiltered(res.data);
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
      toast.error('Failed to fetch candidates');
    }
  };

  const fetchInterviewers = async () => {
    try {
      const res = await interviewersAPI.getAll();
      setInterviewers(res.data);
    } catch (err) {
      console.error('Failed to load interviewers:', err);
      toast.error('Failed to load interviewers');
    }
  };

  const fetchInterviews = async () => {
    try {
      setInterviewsLoading(true);
      const res = await interviewsAPI.getAll();
      setInterviews(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load interviews:', err);
      toast.error('Failed to load interview list');
    } finally {
      setInterviewsLoading(false);
    }
  };

  /* ================= FILTER & SEARCH ================= */

  const applyFilters = () => {
    let result = candidates;

    // Filter by position
    if (selectedPosition !== 'All') {
      result = result.filter(c =>
        (c.position || '').toLowerCase().includes(selectedPosition.toLowerCase())
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(c => {
        const fullName = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
        const position = (c.position || '').toLowerCase();
        const status = (c.status || '').toLowerCase();

        return (
          fullName.includes(search) ||
          position.includes(search) ||
          status.includes(search)
        );
      });
    }

    setFiltered(result);
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPosition, searchTerm, candidates]);

  /* ================= SEARCH HANDLERS ================= */

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearInterviewSearch = () => {
    setInterviewSearchTerm('');
  };

  const candidatesPerPage = 15;
  const totalPages = Math.max(1, Math.ceil(filtered.length / candidatesPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedCandidates = filtered.slice(
    (safeCurrentPage - 1) * candidatesPerPage,
    safeCurrentPage * candidatesPerPage
  );
  const visiblePageNumbers = Array.from(
    { length: Math.min(4, totalPages) },
    (_, index) => {
      const startPage = Math.min(
        Math.max(1, safeCurrentPage - 1),
        Math.max(1, totalPages - 3)
      );
      return startPage + index;
    }
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPosition, searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const filteredInterviews = interviews.filter(interview => {
    if (!interviewSearchTerm.trim()) return true;
    const search = interviewSearchTerm.toLowerCase().trim();
    return [
      interview.candidate_name,
      interview.candidate_email,
      interview.interviewer_name,
      interview.interview_type,
      interview.status,
      interview.meeting_link,
    ].some(value => String(value || '').toLowerCase().includes(search));
  });

  const interviewsPerPage = 10;
  const interviewTotalPages = Math.max(1, Math.ceil(filteredInterviews.length / interviewsPerPage));
  const safeInterviewPage = Math.min(interviewCurrentPage, interviewTotalPages);
  const paginatedInterviews = filteredInterviews.slice(
    (safeInterviewPage - 1) * interviewsPerPage,
    safeInterviewPage * interviewsPerPage
  );

  useEffect(() => {
    setInterviewCurrentPage(1);
  }, [interviewSearchTerm, activeView]);

  useEffect(() => {
    if (interviewCurrentPage > interviewTotalPages) {
      setInterviewCurrentPage(interviewTotalPages);
    }
  }, [interviewCurrentPage, interviewTotalPages]);

  /* ================= SCHEDULE ================= */

  const openModal = (candidate) => {
    if (!canManageInterview) {
      toast.error('You are not permitted to schedule interviews');
      return;
    }
    setSelectedCandidate(candidate);
    setInterviewMode('schedule');
    setGeneratedInterviewLink('');
    setForm({
      scheduled_date: '',
      interview_type: 'Technical',
      mode: 'standard',
      voice_provider: 'livekit',
      interviewer_department: candidate.position || '',
      resume_text: '',
      resume_file_name: '',
      job_description: '',
      // Skills must always be entered manually by the recruiter —
      // never prefilled from candidate/resume/job data.
      primary_skills: '',
      secondary_skills: '',
      interviewers: [
        { interviewer_name: '', interviewer_email: '', interviewer_role: '', interviewer_department: '' }
      ]
    });
    setShowModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsParsingResume(true);

      let resumeText = "";

      if (file.type === "text/plain" || file.name.toLowerCase().endsWith(".txt")) {
        resumeText = await file.text();
      } else {
        const formData = new FormData();
        formData.append("resume", file);
        const response = await aiAPI.parseResume(formData);
        resumeText = response?.data?.rawText || response?.data?.experience || "";
      }

      if (!resumeText.trim()) {
        throw new Error("No text could be extracted from the uploaded resume");
      }

      setForm(prev => ({
        ...prev,
        resume_text: resumeText.substring(0, 12000),
        resume_file_name: file.name
      }));
      toast.success('Resume parsed successfully');
    } catch (err) {
      console.error('Failed to read resume:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to parse resume file');
    } finally {
      setIsParsingResume(false);
    }
  };

  const generateLink = async () => {
    if (!canManageInterview) {
      toast.error('You are not permitted to generate interview links');
      return;
    }
    if (isGeneratingLink) return;
    try {
      setIsGeneratingLink(true);
      setGeneratedInterviewLink('');
      const candidateName = selectedCandidate
        ? `${selectedCandidate.first_name || ''} ${selectedCandidate.last_name || ''}`.trim()
        : '';
      const candidateEmail = selectedCandidate?.email_id || '';
      const { resume_text, job_description, interview_type, primary_skills, secondary_skills } = form;

      if (!candidateName) {
        toast.error('Select a candidate before generating a link');
        return;
      }
      if (!candidateEmail) {
        toast.error('Candidate email is required to generate the interview link');
        return;
      }
      if (!resume_text?.trim()) {
        toast.error('Upload a resume before generating an AI interview link');
        return;
      }
      if (!primary_skills?.trim()) {
        toast.error('Primary skills are required for an AI interview');
        return;
      }
      if (!secondary_skills?.trim()) {
        toast.error('Secondary skills are required for an AI interview');
        return;
      }
      const res = await interviewsAPI.generateLink({
        candidate_id: selectedCandidate?.id,
        candidate_name: candidateName,
        candidate_email: candidateEmail,
        interview_type: 'AI HR Interview',
        mode: form.mode || 'standard',
        voice_provider: form.voice_provider || 'livekit',
        resume_text: resume_text.trim(),
        job_description: job_description || undefined,
        primary_skills: primary_skills?.trim() || undefined,
        secondary_skills: secondary_skills?.trim() || undefined
      });

      const data = res.data;

if (data.interview_link) {

  setGeneratedInterviewLink(data.interview_link);

  // NEW
  setGeneratedUsername(data.username);
  setGeneratedPassword(data.password);

  try {
    await navigator.clipboard.writeText(data.interview_link);
    toast.success("Interview link generated and copied");
  } catch (clipboardErr) {
    console.warn("Clipboard copy failed:", clipboardErr);
    toast.success("Interview link generated");
  }

} else {
  toast.error("Interview link was not returned by server");
}
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to generate interview link');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const scheduleInterview = async () => {
    if (!canManageInterview) {
      toast.error('You are not permitted to schedule interviews');
      return;
    }
    if (isScheduling) {
      return;
    }

    const { scheduled_date, interview_type, interviewer_department, interviewers, resume_text, job_description, primary_skills, secondary_skills } = form;
    const datePart = scheduled_date ? scheduled_date.split('T')[0] : '';
    const timePart = scheduled_date ? scheduled_date.split('T')[1] : '';
    if (!datePart || !timePart || !interviewer_department || interviewers.some(i => !normalizeText(i.interviewer_name))) {
      toast.error('Please fill all required fields for all interviewers');
      return;
    }

    const invalidInterviewer = interviewers.find((intv) => {
      const name = normalizeText(intv.interviewer_name);
      const email = normalizeText(intv.interviewer_email);
      return !isValidPersonName(name) || (email && !isValidEmail(email));
    });

    if (invalidInterviewer) {
      toast.error('Please enter valid interviewer names and email addresses');
      return;
    }

    // NOTE: Primary/Secondary skills are AI-Interview-only fields (rendered only
    // in the AI section of this modal). Normal Teams scheduling must NOT
    // require or validate them here; AI link generation (generateLink) enforces
    // both with field-specific messages, and POST /generate-link enforces them
    // server-side.

    try {
      setIsScheduling(true);
      await interviewsAPI.create({
        candidate_id: selectedCandidate.id,
        scheduled_date,
        interview_type,
        interviewer_department,
        voice_provider: form.voice_provider || 'livekit',
        resume_text: resume_text.trim(),
        job_description: job_description || undefined,
        ...(primary_skills?.trim() ? { primary_skills: primary_skills.trim() } : {}),
        ...(secondary_skills?.trim() ? { secondary_skills: secondary_skills.trim() } : {}),
        interviewers
      });
      await fetchCandidates();
      await fetchInterviews();
      toast.success('Interview scheduled & invitation sent');
      setShowModal(false);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, 'Failed to schedule interview'));
    } finally {
      setIsScheduling(false);
    }
  };

  const cancelInterview = (interview) => {
    if (!canManageInterview) {
      toast.error('You are not permitted to cancel interviews');
      return;
    }
    if (cancellingInterviewId) return;
    setInterviewToCancel(interview);
  };

  const confirmCancelInterview = async () => {
    if (!interviewToCancel || cancellingInterviewId) return;
    const interview = interviewToCancel;
    const label = interview?.candidate_name
      ? `${interview.candidate_name} on ${formatWallClockDateTime(interview.scheduled_date)}`
      : 'this interview';
    try {
      setCancellingInterviewId(interview.id);
      await interviewsAPI.cancel(interview.id);
      await fetchInterviews();
      await fetchCandidates();
      toast.success('Interview cancelled');
      setInterviewToCancel(null);
    } catch (err) {
      console.error('Failed to cancel interview:', err);
      toast.error(getErrorMessage(err, 'Failed to cancel interview'));
    } finally {
      setCancellingInterviewId(null);
    }
  };

  /* ================= RESCHEDULE ================= */

  const openRescheduleModal = (interview) => {
    if (!canManageInterview) {
      toast.error('You are not permitted to reschedule interviews');
      return;
    }
    setRescheduleInterview(interview);
    setRescheduleDate(interview.scheduled_date || '');
    setShowRescheduleModal(true);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleDate || !rescheduleInterview) return;
    try {
      setRescheduling(true);
      await interviewsAPI.update(rescheduleInterview.id, { scheduled_date: rescheduleDate });
      await fetchInterviews();
      toast.success('Interview rescheduled successfully');
      setShowRescheduleModal(false);
      setRescheduleInterview(null);
    } catch (err) {
      console.error('Failed to reschedule interview:', err);
      toast.error(getErrorMessage(err, 'Failed to reschedule interview'));
    } finally {
      setRescheduling(false);
    }
  };

  /* ================= INTERVIEWERS ================= */

  const addInterviewer = async () => {
    if (savingInterviewer) return;
    const { name, email, role, department } = newInterviewer;
    const normalizedName = normalizeText(name);
    const normalizedEmail = normalizeText(email).toLowerCase();
    const normalizedRole = normalizeText(role);
    const normalizedDepartment = normalizeText(department);

    if (!normalizedName || !normalizedRole || !normalizedDepartment) {
      toast.error('Please fill required fields');
      return;
    }

    if (!isValidPersonName(normalizedName)) {
      toast.error('Please enter a valid interviewer name');
      return;
    }

    if (normalizedEmail && !isValidEmail(normalizedEmail)) {
      toast.error('Please enter a valid interviewer email address');
      return;
    }

    try {
      setSavingInterviewer(true);
      await interviewersAPI.create({
        name: normalizedName,
        email: normalizedEmail,
        role: normalizedRole,
        department: normalizedDepartment
      });
      setNewInterviewer({ name: '', email: '', role: '', department: '' });
      fetchInterviewers();
      toast.success('Interviewer added');
    } catch (err) {
      console.error('Failed to add interviewer:', err);
      toast.error(getErrorMessage(err, 'Failed to add interviewer'));
    } finally {
      setSavingInterviewer(false);
    }
  };

  const deleteInterviewer = (id) => {
    if (deletingInterviewerId) return;
    setInterviewerToDelete(id);
  };

  const confirmDeleteInterviewer = async () => {
    if (!interviewerToDelete || deletingInterviewer) return;
    const id = interviewerToDelete;
    try {
      setDeletingInterviewer(true);
      setDeletingInterviewerId(id);
      await interviewersAPI.delete(id);
      fetchInterviewers();
      toast.success('Interviewer removed');
      setInterviewerToDelete(null);
    } catch (err) {
      console.error('Failed to delete interviewer:', err);
      toast.error('Failed to delete interviewer');
    } finally {
      setDeletingInterviewer(false);
      setDeletingInterviewerId(null);
    }
  };

  /* ================= UI ================= */

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Input focus animation */
        input:focus,
        select:focus,
        textarea:focus {
          outline: none;
          border-color: #1E40AF !important;
          box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1) !important;
        }

        /* Button hover animation */
        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        button:active:not(:disabled) {
          transform: translateY(0);
        }

        /* Scrollbar styling for modal */
        div::-webkit-scrollbar {
          width: 8px;
        }

        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        div::-webkit-scrollbar-thumb {
          background: #bbb;
          border-radius: 10px;
        }

        div::-webkit-scrollbar-thumb:hover {
          background: #888;
        }

        /* ================= RESPONSIVE DESIGN ================= */

        /* TABLET (max-width: 768px) */
        @media (max-width: 768px) {
          input,
          select,
          textarea {
            font-size: 16px; /* Prevents zoom on iOS */
            width: 100%;
            box-sizing: border-box;
          }

          /* Make modal full width on tablets */
          div[style*="maxWidth: 600px"] {
            max-width: 95vw !important;
            border-radius: 12px !important;
          }

          /* Stack buttons on tablets */
          div[style*="flexDirection"] {
            flex-direction: column-reverse;
          }

          /* Increase touch target sizes */
          button {
            min-height: 44px;
            min-width: 44px;
          }
        }

        /* MOBILE (max-width: 480px) */
        @media (max-width: 480px) {
          input,
          select,
          textarea {
            font-size: 16px;
            padding: 10px 11px;
          }

          /* Stack modal full screen */
          div[style*="maxWidth: 600px"],
          div[style*="maxWidth: 95vw"] {
            max-width: 100% !important;
            max-height: 100vh !important;
            border-radius: 10px !important;
          }

          /* Reduce padding on mobile */
          div[style*="padding: 28px"] {
            padding: 16px 12px !important;
          }

          div[style*="padding: 20px"] {
            padding: 12px !important;
          }

          /* Full width buttons */
          button {
            width: 100%;
            min-height: 44px;
          }

          /* Reduce font sizes on mobile */
          h3[style*="fontSize"] {
            font-size: 18px !important;
          }

          label {
            font-size: 13px !important;
          }

          /* Better spacing for forms */
          div[style*="marginBottom"] {
            margin-bottom: 16px !important;
          }

          /* Table responsiveness */
          table {
            font-size: 12px;
          }

          th, td {
            padding: 12px 8px !important;
          }
        }

        /* SMALL MOBILE (max-width: 360px) */
        @media (max-width: 360px) {
          input,
          select,
          textarea {
            padding: 10px 8px;
            font-size: 14px;
          }

          button {
            padding: 10px 12px;
            font-size: 12px;
          }

          h2, h3 {
            font-size: 16px !important;
          }

          label {
            font-size: 12px !important;
          }

          div[style*="padding: 28px"] {
            padding: 12px 8px !important;
          }
        }
      `}</style>

      {/* HEADER */}
        <div style={styles.header}>
        <div>
          <h2 style={styles.pageTitle}>Schedule Interview</h2>
          <p style={styles.pageSubtitle}>Manage candidate interview scheduling</p>
        </div>

        {canManageInterview && (
        <LoadingButton
          variant="secondary"
          size="md"
          onClick={() => setShowInterviewerModal(true)}
          style={styles.manageBtn}
        >
          Manage Interviewers
        </LoadingButton>
        )}
      </div>

      {/* TAB NAVIGATION */}
      <div style={styles.tabContainer}>
          {canManageInterview && (
          <button
            style={{
              ...styles.tab,
              ...(activeView === 'schedule' ? styles.tabActive : {})
            }}
            onClick={() => setActiveView('schedule')}
          >
            Schedule Interview
          </button>
          )}
          <button
            style={{
              ...styles.tab,
              ...(activeView === 'list' ? styles.tabActive : {})
            }}
            onClick={() => setActiveView('list')}
          >
            Interview List
          </button>
      </div>

      <div style={{ display: activeView === 'schedule' && canManageInterview ? 'block' : 'none' }}>

      {/* FILTER & SEARCH SECTION */}
      <div style={styles.filterSearchSection}>
        {/* Position Filter */}
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Filter by Position:</label>
          <select
            style={styles.filterSelect}
            value={selectedPosition}
            onChange={e => setSelectedPosition(e.target.value)}
          >
            {positions.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>

        {/* Search Input */}
        <div style={styles.searchGroup}>
          <div style={styles.searchInputWrapper}>
            <span style={styles.searchIcon}></span>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="Search by name, position or status..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button type="button" style={styles.clearSearchBtn} onClick={clearSearch}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div style={styles.resultsCount}>
        Showing <strong>{filtered.length}</strong> candidate{filtered.length !== 1 ? 's' : ''}
        {searchTerm && ` matching "${searchTerm}"`}
      </div>

      {/* TABLE OR EMPTY STATE */}
      {filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}></div>
          <h3 style={styles.emptyTitle}>
            {searchTerm ? 'No Results Found' : 'No Candidates Available'}
          </h3>
          <p style={styles.emptyText}>
            {searchTerm 
              ? `No candidates found matching "${searchTerm}". Try a different search term.`
              : 'There are no candidates to schedule interviews for at this time.'}
          </p>
          {searchTerm && (
                <button style={styles.clearSearchButton} onClick={clearSearch}>
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th scope="col" style={styles.th}>Candidate Name</th>
                <th scope="col" style={styles.th}>Position</th>
                <th scope="col" style={styles.th}>Status</th>
                <th scope="col" style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCandidates.map((c, idx) => (
                <tr key={c.id} style={{
                  ...styles.tableRow,
                  animationDelay: `${idx * 0.05}s`
                }}>
                  <td style={styles.td}>
                    <div style={styles.candidateCell}>
                      <div style={styles.avatar}>
                        {(c.first_name || 'C')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={styles.candidateName}>
                          {c.first_name} {c.last_name}
                        </div>
                        <div style={styles.candidateId}>ID: #{c.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>{c.position || '—'}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      ...getStatusStyle(c.status),
                      padding: '6px 12px',
                      borderRadius: '4px'
                    }}>
                      {getStatusLabel(c.status) || c.status || 'Pending'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <LoadingButton
                      size="sm"
                      onClick={() => openModal(c)}
                      disabled={!canManageInterview || isScheduling}
                    >
                      Schedule
                    </LoadingButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={styles.paginationWrap}>
            <button
              type="button"
              style={{
                ...styles.paginationBtn,
                ...styles.paginationArrow,
                ...(safeCurrentPage === 1 ? styles.paginationBtnDisabled : {})
              }}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={safeCurrentPage === 1}
              aria-label="Previous page"
            >
              ←
            </button>

            {visiblePageNumbers.map(pageNumber => (
              <button
                key={pageNumber}
                type="button"
                style={{
                  ...styles.paginationBtn,
                  ...(safeCurrentPage === pageNumber ? styles.paginationBtnActive : {})
                }}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}

            <button
              type="button"
              style={{
                ...styles.paginationBtn,
                ...styles.paginationArrow,
                ...(safeCurrentPage === totalPages ? styles.paginationBtnDisabled : {})
              }}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={safeCurrentPage === totalPages}
              aria-label="Next page"
            >
              →
            </button>
          </div>
        )}
        </>
      )}

      </div>

      {activeView === 'list' && (
        <div style={styles.interviewListSection}>
          <div style={styles.listToolbar}>
            <div>
              <div style={styles.resultsCount}>
                Showing <strong>{filteredInterviews.length}</strong> scheduled interview{filteredInterviews.length !== 1 ? 's' : ''}
              </div>
              <p style={styles.listSubtitle}>
                Review scheduled interviews, meeting links, and organizer details.
              </p>
            </div>

            <div style={styles.searchGroup}>
              <div style={styles.searchInputWrapper}>
                <span style={styles.searchIcon}></span>
                <input
                  type="text"
                  style={styles.searchInput}
                  placeholder="Search candidate, interviewer, status..."
                  value={interviewSearchTerm}
                  onChange={e => setInterviewSearchTerm(e.target.value)}
                />
                {interviewSearchTerm && (
                  <button type="button" style={styles.clearSearchBtn} onClick={clearInterviewSearch}>
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {interviewsLoading ? (
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'center', marginBottom: '16px' }}>
                <Skeleton width="120px" height={14} />
                <Skeleton width="180px" height={14} />
                <Skeleton width="100px" height={14} />
                <Skeleton width="140px" height={14} />
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <Skeleton width="160px" height={12} />
                  <Skeleton width="200px" height={12} />
                  <Skeleton width="80px" height={12} />
                  <Skeleton width="120px" height={12} />
                  <Skeleton width="70px" height={28} borderRadius={6} />
                </div>
              ))}
            </div>
          ) : filteredInterviews.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}></div>
              <h3 style={styles.emptyTitle}>
                {interviewSearchTerm ? 'No Results Found' : 'No Scheduled Interviews'}
              </h3>
              <p style={styles.emptyText}>
                {interviewSearchTerm
                  ? `No interviews found matching "${interviewSearchTerm}".`
                  : 'Scheduled interviews will appear here after HR creates them.'}
              </p>
              {interviewSearchTerm && (
                <button style={styles.clearSearchButton} onClick={clearInterviewSearch}>
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <>
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th scope="col" style={styles.th}>Candidate</th>
                      <th scope="col" style={styles.th}>Interview Details</th>
                      <th scope="col" style={styles.th}>Meeting</th>
                      <th scope="col" style={styles.th}>Status</th>
                      <th scope="col" style={styles.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInterviews.map((interview, idx) => {
                      const isCancelled = String(interview.status || '').toLowerCase() === 'cancelled';

                      return (
                      <tr key={interview.id} style={{
                        ...styles.tableRow,
                        animationDelay: `${idx * 0.05}s`
                      }}>
                        <td style={styles.td}>
                          <div style={styles.interviewCell}>
                            <div style={styles.avatar}>
                              {(interview.candidate_name || 'I')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={styles.candidateName}>
                                {interview.candidate_name || 'Unknown Candidate'}
                              </div>
                              <div style={styles.candidateId}>
                                {interview.candidate_email || 'No email'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.interviewMeta}>
                            <div><strong>Type:</strong> {interview.interview_type || 'Interview'}</div>
                            <div><strong>Interviewer:</strong> {interview.interviewer_name || 'TBD'}</div>
                            <div><strong>Time:</strong> {formatWallClockDateTime(interview.scheduled_date)}</div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          {isCancelled ? (
                            <span style={{ ...styles.mutedText, color: '#999' }}>
                              Meeting Cancelled
                            </span>
                          ) : interview.meeting_link ? (
                            <a
                              href={interview.meeting_link}
                              target="_blank"
                              rel="noreferrer"
                              style={styles.linkBtn}
                            >
                              {String(interview.status || '').toLowerCase() === 'completed'
                                ? 'View Meeting'
                                : 'Open Meeting'}
                            </a>
                          ) : (
                            <span style={styles.mutedText}>No link</span>
                          )}
                          {interview.organizer_email && (
                            <div style={styles.organizerText}>
                              Organizer: {interview.organizer_email}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            ...styles.interviewStatusBadge,
                            ...(isCancelled ? styles.interviewStatusBadgeCancelled : {})
                          }}>
                            {getStatusLabel(interview.status) || interview.status || 'scheduled'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {(() => {
                              const status = String(interview.status || '').toLowerCase();
                              if (status === 'scheduled') {
                                return (
                                  <>
                                    {canManageInterview && (
                                    <LoadingButton
                                      variant="primary"
                                      size="sm"
                                      onClick={() => openRescheduleModal(interview)}
                                      style={styles.editBtn}
                                    >
                                      Edit
                                    </LoadingButton>
                                    )}
                                    {interview.meeting_link ? (
                                      canManageInterview && (
                                      <LoadingButton
                                        variant="danger"
                                        size="sm"
                                        loading={cancellingInterviewId === interview.id}
                                        onClick={() => cancelInterview(interview)}
                                        disabled={cancellingInterviewId === interview.id}
                                        loadingText="Cancelling..."
                                        style={styles.deleteBtn}
                                      >
                                        Cancel
                                      </LoadingButton>
                                      )
                                    ) : (
                                      <span style={styles.mutedText}>No Teams meeting</span>
                                    )}
                                  </>
                                );
                              } else if (status === 'completed') {
                                return (
                                  <>
                                    <LoadingButton
                                      variant="primary"
                                      size="sm"
                                      onClick={() => navigate(`/candidates/${interview.candidate_id}`)}
                                      style={styles.editBtn}
                                    >
                                      View Details
                                    </LoadingButton>
                                  </>
                                );
                              } else if (status === 'cancelled') {
                                return interview.meeting_link && canManageInterview ? (
                                  <LoadingButton
                                    variant="danger"
                                    size="sm"
                                    loading={cancellingInterviewId === interview.id}
                                    onClick={() => cancelInterview(interview)}
                                    disabled={cancellingInterviewId === interview.id}
                                    loadingText="Cancelling..."
                                    style={styles.deleteBtn}
                                  >
                                    Retry Cancel
                                  </LoadingButton>
                                ) : null;
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {interviewTotalPages > 1 && (
                <div style={styles.paginationWrap}>
                  <button
                    type="button"
                    style={{
                      ...styles.paginationBtn,
                      ...styles.paginationArrow,
                      ...(safeInterviewPage === 1 ? styles.paginationBtnDisabled : {})
                    }}
                    onClick={() => setInterviewCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={safeInterviewPage === 1}
                    aria-label="Previous interview page"
                  >
                    ←
                  </button>

                  {Array.from({ length: interviewTotalPages }, (_, index) => index + 1)
                    .slice(Math.max(0, safeInterviewPage - 2), Math.max(0, safeInterviewPage - 2) + 4)
                    .map(pageNumber => (
                      <button
                        key={pageNumber}
                        type="button"
                        style={{
                          ...styles.paginationBtn,
                          ...(safeInterviewPage === pageNumber ? styles.paginationBtnActive : {})
                        }}
                        onClick={() => setInterviewCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}

                  <button
                    type="button"
                    style={{
                      ...styles.paginationBtn,
                      ...styles.paginationArrow,
                      ...(safeInterviewPage === interviewTotalPages ? styles.paginationBtnDisabled : {})
                    }}
                    onClick={() => setInterviewCurrentPage(prev => Math.min(interviewTotalPages, prev + 1))}
                    disabled={safeInterviewPage === interviewTotalPages}
                    aria-label="Next interview page"
                  >
                    →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* SCHEDULE INTERVIEW MODAL */}
      {showModal && (
        <div style={styles.backdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Schedule Interview</h3>
              <LoadingButton variant="secondary" size="md" onClick={() => setShowModal(false)}>Close</LoadingButton>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.candidateInfo}>
                <p style={styles.infoLabel}><strong>Candidate:</strong> {selectedCandidate?.first_name} {selectedCandidate?.last_name}</p>
                <p style={styles.infoLabel}><strong>Position:</strong> {selectedCandidate?.position}</p>
              </div>

              <div style={styles.modeSwitcher}>
                <button
                  type="button"
                  style={{
                    ...styles.modeButton,
                    ...(interviewMode === 'schedule' ? styles.modeButtonActive : {})
                  }}
                  onClick={() => setInterviewMode('schedule')}
                >
                  Schedule Interview
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.modeButton,
                    ...(interviewMode === 'ai' ? styles.modeButtonActive : {})
                  }}
                  onClick={() => setInterviewMode('ai')}
                >
                  AI Interview
                </button>
              </div>

              {interviewMode === 'schedule' ? (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Date & Time</label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <input
                        type="date"
                        style={{ ...styles.input, flex: 1 }}
                        value={form.scheduled_date ? form.scheduled_date.split('T')[0] : ''}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => {
                          const timePart = form.scheduled_date ? form.scheduled_date.split('T')[1] : '09:00';
                          setForm(prev => ({
                            ...prev,
                            scheduled_date: `${e.target.value}T${timePart || '09:00'}`
                          }));
                        }}
                      />
                      {(() => {
                        const timeStr = form.scheduled_date ? form.scheduled_date.split('T')[1] : '09:00';
                        const [hhRaw, mmRaw] = (timeStr || '09:00').split(':');
                        const hour24 = parseInt(hhRaw, 10) || 9;
                        const minute = mmRaw ? mmRaw.padStart(2, '0') : '00';
                        const ampm = hour24 >= 12 ? 'PM' : 'AM';
                        const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

                        const applyTimeChange = (h12, min, period) => {
                          let h = parseInt(h12, 10);
                          if (period === 'PM' && h !== 12) h += 12;
                          else if (period === 'AM' && h === 12) h = 0;
                          const datePart = form.scheduled_date ? form.scheduled_date.split('T')[0] : '';
                          setForm(prev => ({
                            ...prev,
                            scheduled_date: `${datePart}T${String(h).padStart(2, '0')}:${min}`
                          }));
                        };

                        return (
                          <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                            <select
                              style={{ ...styles.input, flex: 1, minWidth: 0 }}
                              value={hour12}
                              onChange={e => applyTimeChange(e.target.value, minute, ampm)}
                            >
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                            <select
                              style={{ ...styles.input, flex: 1, minWidth: 0 }}
                              value={minute}
                              onChange={e => applyTimeChange(hour12, e.target.value, ampm)}
                            >
                              {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            <select
                              style={{ ...styles.input, flex: 1, minWidth: 0 }}
                              value={ampm}
                              onChange={e => applyTimeChange(hour12, minute, e.target.value)}
                            >
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Interview Type</label>
                    <select
                      style={styles.input}
                      value={form.interview_type}
                      onChange={e => setForm(prev => ({ ...prev, interview_type: e.target.value }))}
                    >
                      <option>Technical</option>
                      <option>Non-Technical</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Interview Position</label>
                    <select
                      style={styles.input}
                      value={form.interviewer_department}
                      onChange={e => handleFormChange('interviewer_department', e.target.value)}
                    >
                      <option value="">Select Position</option>
                      {selectedCandidate?.position && (
                        <option value={selectedCandidate.position}>
                          {selectedCandidate.position}
                        </option>
                      )}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Interviewers</label>
                    {(Array.isArray(form.interviewers) ? form.interviewers : []).map((intv, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <select
                          style={styles.input}
                          value={intv.interviewer_name}
                          onChange={e => {
                            const interviewer = interviewers.find(i => i.name === e.target.value);
                            setForm(prev => {
                              const updated = [...prev.interviewers];
                              if (interviewer) {
                                updated[idx] = {
                                  interviewer_name: interviewer.name,
                                  interviewer_email: interviewer.email || '',
                                  interviewer_role: interviewer.role || '',
                                  interviewer_department: interviewer.department || ''
                                };
                              } else {
                                updated[idx] = { interviewer_name: '', interviewer_email: '', interviewer_role: '', interviewer_department: '' };
                              }
                              return { ...prev, interviewers: updated };
                            });
                          }}
                        >
                          <option value="">Select Interviewer</option>
                          {interviewers.map(i => (
                            <option key={i.id} value={i.name}>{i.name}</option>
                          ))}
                        </select>
                        {form.interviewers && form.interviewers.length > 1 && (
                          <button type="button" style={styles.removeInterviewerBtn} onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              interviewers: prev.interviewers.filter((_, i) => i !== idx)
                            }));
                          }}>Remove</button>
                        )}
                      </div>
                    ))}
                    <button type="button" style={styles.addInterviewerRowBtn} onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        interviewers: [
                          ...(Array.isArray(prev.interviewers) ? prev.interviewers : []),
                          { interviewer_name: '', interviewer_email: '', interviewer_role: '', interviewer_department: '' }
                        ]
                      }));
                    }}>Add Interviewer</button>
                  </div>
                </>
              ) : (
                <div style={styles.aiSection}>
                  <div style={styles.aiSectionHeader}>
                    <h4 style={styles.aiSectionTitle}>AI Interview</h4>
                    <p style={styles.aiSectionText}>
                      Upload or paste the resume and add the job description. The AI interview link will use those details to generate tailored questions.
                    </p>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Interview Mode</label>
                    <select
                      style={styles.input}
                      value={form.mode || 'standard'}
                      onChange={e => setForm(prev => ({ ...prev, mode: e.target.value }))}
                    >
                      <option value="standard">Bot Interview (Text & Audio Recording)</option>
                      <option value="voice">AI Voice Interview (Real-time)</option>
                    </select>
                  </div>

                  {form.mode === 'voice' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>AI Voice Provider</label>
                      <select
                        style={styles.input}
                        value={form.voice_provider}
                        onChange={e => setForm(prev => ({ ...prev, voice_provider: e.target.value }))}
                      >
                        <option value="livekit">LiveKit</option>
                        <option value="chime">Chime</option>
                      </select>
                    </div>
                  )}

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Resume Upload</label>
                    <input
                      type="file"
                      accept=".txt,.pdf,.docx,.md,.text"
                      style={styles.input}
                      onChange={handleResumeUpload}
                      disabled={isParsingResume}
                    />
                    {form.resume_file_name && (
                      <div style={styles.helperText}>Loaded file: {form.resume_file_name}</div>
                    )}
                    {isParsingResume && (
                      <div style={styles.helperText}>Parsing resume using Document AI...</div>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Resume Text</label>
                    <textarea
                      style={{ ...styles.input, minHeight: 120, resize: 'vertical' }}
                      value={form.resume_text}
                      onChange={e => handleFormChange('resume_text', e.target.value)}
                      placeholder="Paste the candidate resume here."
                    />
                  </div>

                  <div style={{ ...styles.formGroup, marginBottom: 0 }}>
                    <label style={styles.label}>Job Description</label>
                    <textarea
                      style={{ ...styles.input, minHeight: 120, resize: 'vertical' }}
                      value={form.job_description}
                      onChange={e => handleFormChange('job_description', e.target.value)}
                      placeholder="Paste the job description here."
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Primary Skills *</label>
                    <textarea
                      style={{ ...styles.input, minHeight: 80, resize: 'vertical' }}
                      value={form.primary_skills}
                      onChange={e => handleFormChange('primary_skills', e.target.value)}
                      placeholder="Comma-separated primary skills (e.g. React, Node.js, SQL)"
                    />
                    <div style={styles.helperText}>Required. Together with secondary skills, drives 10 of the 12 technical interview questions.</div>
                  </div>

                  <div style={{ ...styles.formGroup, marginBottom: 0 }}>
                    <label style={styles.label}>Secondary Skills *</label>
                    <textarea
                      style={{ ...styles.input, minHeight: 80, resize: 'vertical' }}
                      value={form.secondary_skills}
                      onChange={e => handleFormChange('secondary_skills', e.target.value)}
                      placeholder="Comma-separated secondary skills"
                    />
                    <div style={styles.helperText}>Required. Together with primary skills, drives 10 of the 12 technical interview questions.</div>
                  </div>

                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              {generatedInterviewLink && (
                <div style={styles.generatedLinkCardFooter}>
                  <div style={styles.generatedLinkTitle}>Generated Interview Link</div>
                  <a
                    href={generatedInterviewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.generatedLinkAnchor}
                  >
                    {generatedInterviewLink}
                  </a>
                  <div style={{
  marginTop: "10px",
  padding: "8px 12px",
  background: form.voice_provider === 'livekit' ? "#eef6ff" : "#f5f5f5",
  borderRadius: "8px",
  fontSize: "0.9rem",
  color: form.voice_provider === 'livekit' ? "#1d4ed8" : "#64748b"
}}>
  Voice Provider: <strong>{form.voice_provider === 'livekit' ? 'LiveKit (real-time audio)' : 'Chime'}</strong>
</div>
                  <div style={{
  marginTop: "15px",
  padding: "12px",
  background: "#f5f5f5",
  borderRadius: "8px"
}}>
  <p>
    <strong>User ID:</strong> {generatedUsername}
  </p>

  <p style={{ marginTop: "8px" }}>
    <strong>Password:</strong> {generatedPassword}
  </p>
</div>
                  <button
                    type="button"
                    style={styles.copyLinkBtn}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(generatedInterviewLink);
                        toast.success('Interview link copied');
                      } catch (clipboardErr) {
                        toast.error('Unable to copy link. Please copy it manually.');
                      }
                    }}
                  >
                    Copy Link
                  </button>
                </div>
              )}
              <div style={styles.modalFooterActions}>
              {interviewMode === 'schedule' ? (
                <LoadingButton
                  loading={isScheduling}
                  onClick={scheduleInterview}
                  loadingText="Scheduling..."
                >
                  Schedule & Send Email
                </LoadingButton>
              ) : (
                <LoadingButton
                  loading={isGeneratingLink}
                  onClick={generateLink}
                  disabled={isScheduling}
                  loadingText="Generating Link..."
                >
                  Generate Interview Link
                </LoadingButton>
              )}
              <LoadingButton variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* MANAGE INTERVIEWERS MODAL */}
      {showInterviewerModal && (
        <div style={styles.backdrop}>
          <div style={{ ...styles.modal, width: '900px', maxWidth: '900px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Manage Interviewers</h3>
              <LoadingButton variant="secondary" onClick={() => setShowInterviewerModal(false)}>Close</LoadingButton>
            </div>

            <div style={styles.modalContent}>
              <h4 style={styles.sectionTitle}>Add New Interviewer</h4>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  placeholder="Enter full name"
                  pattern="^(?=.*[A-Za-z])[A-Za-z][A-Za-z\\s.'-]*$"
                  title="Use letters, spaces, hyphens, apostrophes, or periods only"
                  style={styles.input}
                  value={newInterviewer.name}
                  onChange={e =>
                    setNewInterviewer({ ...newInterviewer, name: e.target.value })
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  title="Enter a valid email address"
                  style={styles.input}
                  value={newInterviewer.email}
                  onChange={e =>
                    setNewInterviewer({ ...newInterviewer, email: e.target.value })
                  }
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  style={styles.input}
                  value={newInterviewer.role}
                  onChange={e =>
                    setNewInterviewer({ ...newInterviewer, role: e.target.value })
                  }
                >
                  <option value="">Select Role</option>
                  <option value="HR">HR</option>
                  <option value="Technical">Technical</option>
                  <option value="Managerial">Managerial</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Interview Position *</label>
                <select
                  style={styles.input}
                  value={newInterviewer.department}
                  onChange={e =>
                    setNewInterviewer({ ...newInterviewer, department: e.target.value })
                  }
                >
                  <option value="">Select Position</option>
                  {positions.filter(p => p !== 'All').map(p => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>

              <LoadingButton
                loading={savingInterviewer}
                onClick={addInterviewer}
                loadingText="Adding Interviewer..."
                style={styles.addBtn}
              >
                Add Interviewer
              </LoadingButton>

              <hr style={styles.divider} />

              <div style={styles.interviewersList}>
                <h4 style={styles.sectionTitle}>Current Interviewers ({interviewers.length})</h4>
                {interviewers.length === 0 ? (
                  <p style={styles.emptyText}>No interviewers added yet</p>
                ) : (
                    <div style={styles.tableWrapper}>
                    <table style={{ ...styles.table, minWidth: '720px' }}>
                      <thead>
                        <tr>
                          <th scope="col" style={styles.th}>Name</th>
                          <th scope="col" style={styles.th}>Email</th>
                          <th scope="col" style={styles.th}>Role</th>
                          <th scope="col" style={styles.th}>Position</th>
                          <th scope="col" style={styles.th}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {interviewers.map(i => (
                          <tr key={i.id} style={styles.tableRow}>
                            <td style={styles.td}>{i.name}</td>
                            <td style={styles.td}>{i.email || '—'}</td>
                            <td style={styles.td}>{i.role || '—'}</td>
                            <td style={styles.td}>{i.department || '—'}</td>
                            <td style={styles.td}>
                              <LoadingButton
                                variant="danger"
                                size="sm"
                                loading={deletingInterviewerId === i.id}
                                onClick={() => deleteInterviewer(i.id)}
                                disabled={deletingInterviewerId === i.id}
                                loadingText="Deleting..."
                              >
                                Delete
                              </LoadingButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <LoadingButton variant="secondary" onClick={() => setShowInterviewerModal(false)}>
                Close
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      {/* RESCHEDULE MODAL */}
      {showRescheduleModal && rescheduleInterview && (
        <div style={styles.backdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Reschedule Interview</h3>
              <LoadingButton variant="secondary" size="md" onClick={() => { setShowRescheduleModal(false); setRescheduleInterview(null); }}>Close</LoadingButton>
            </div>
            <div style={styles.modalContent}>
              <div style={styles.candidateInfo}>
                <p style={styles.infoLabel}><strong>Candidate:</strong> {rescheduleInterview.candidate_name || 'Unknown'}</p>
                <p style={styles.infoLabel}><strong>Interviewer:</strong> {rescheduleInterview.interviewer_name || 'TBD'}</p>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>New Date & Time</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="date"
                    style={{ ...styles.input, flex: 1 }}
                    value={rescheduleDate ? rescheduleDate.split('T')[0] : ''}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => {
                      const timePart = rescheduleDate ? rescheduleDate.split('T')[1] : '09:00';
                      setRescheduleDate(`${e.target.value}T${timePart || '09:00'}`);
                    }}
                  />
                  {(() => {
                    const timeStr = rescheduleDate ? rescheduleDate.split('T')[1] : '09:00';
                    const [hhRaw, mmRaw] = (timeStr || '09:00').split(':');
                    const hour24 = parseInt(hhRaw, 10) || 9;
                    const minute = mmRaw ? mmRaw.padStart(2, '0') : '00';
                    const ampm = hour24 >= 12 ? 'PM' : 'AM';
                    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

                    const applyTimeChange = (h12, min, period) => {
                      let h = parseInt(h12, 10);
                      if (period === 'PM' && h !== 12) h += 12;
                      else if (period === 'AM' && h === 12) h = 0;
                      const datePart = rescheduleDate ? rescheduleDate.split('T')[0] : '';
                      setRescheduleDate(`${datePart}T${String(h).padStart(2, '0')}:${min}`);
                    };

                    return (
                      <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                        <select
                          style={{ ...styles.input, flex: 1, minWidth: 0 }}
                          value={hour12}
                          onChange={e => applyTimeChange(e.target.value, minute, ampm)}
                        >
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                        <select
                          style={{ ...styles.input, flex: 1, minWidth: 0 }}
                          value={minute}
                          onChange={e => applyTimeChange(hour12, e.target.value, ampm)}
                        >
                          {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                        <select
                          style={{ ...styles.input, flex: 1, minWidth: 0 }}
                          value={ampm}
                          onChange={e => applyTimeChange(hour12, minute, e.target.value)}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <LoadingButton
                loading={rescheduling}
                onClick={handleRescheduleSubmit}
                loadingText="Rescheduling..."
                style={styles.addBtn}
              >
                Reschedule
              </LoadingButton>
              <LoadingButton variant="secondary" onClick={() => { setShowRescheduleModal(false); setRescheduleInterview(null); }}>
                Cancel
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

    <ConfirmModal
      isOpen={!!interviewToCancel}
      variant="warning"
      title="Cancel interview?"
      message={interviewToCancel ? `Cancel ${interviewToCancel.candidate_name ? interviewToCancel.candidate_name + " on " + formatWallClockDateTime(interviewToCancel.scheduled_date) : "this interview"}? This will remove the Teams meeting.` : ""}
      confirmLabel="Cancel interview"
      cancelLabel="Keep"
      isLoading={!!cancellingInterviewId}
      loadingText="Cancelling..."
      onConfirm={confirmCancelInterview}
      onCancel={() => setInterviewToCancel(null)}
    />
    <ConfirmModal
      isOpen={!!interviewerToDelete}
      variant="danger"
      title="Delete interviewer?"
      message="Are you sure you want to delete this interviewer?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      isLoading={deletingInterviewer}
      loadingText="Deleting..."
      onConfirm={confirmDeleteInterviewer}
      onCancel={() => setInterviewerToDelete(null)}
    />
    </div>
  );
}

/* ================= PROFESSIONAL BLACK & WHITE STYLES ================= */

const styles = {
  page: {
    padding: '4px 0',
    background: 'transparent',
    minHeight: 'auto',
    fontFamily: "var(--font-sans)",
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    paddingBottom: '16px',
    borderBottom: '2px solid #E5E7EB',
    flexWrap: 'wrap',
    gap: '16px'
  },

  pageTitle: {
    margin: '0 0 4px 0',
    fontSize: '24px',
    fontWeight: '700',
    fontFamily: "var(--font-sans)",
    color: '#0F172A'
  },

  pageSubtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#64748B',
    fontWeight: '400'
  },

  tabContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    borderBottom: '2px solid #E5E7EB'
  },

  tab: {
    flex: 1,
    padding: '10px 18px',
    background: '#FFFFFF',
    border: '1px solid #D8E1EF',
    borderBottom: '3px solid #D8E1EF',
    borderRadius: '10px 10px 0 0',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "var(--font-sans)",
    color: '#1E293B',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 6px 14px rgba(15, 23, 42, 0.06)'
  },

  tabActive: {
    color: '#FFFFFF',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    borderColor: '#0F172A',
    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.18)'
  },

  interviewListSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },

  listToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap'
  },

  listSubtitle: {
    margin: '8px 0 0 0',
    color: '#64748B',
    fontSize: '14px',
    fontFamily: "var(--font-sans)"
  },

  filterSearchSection: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },

  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#F9FAFB',
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #E5E7EB'
  },

  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: "var(--font-sans)",
    color: '#374151',
    whiteSpace: 'nowrap'
  },

  filterSelect: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #D1D5DB',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: "var(--font-sans)",
    cursor: 'pointer',
    background: '#FFFFFF',
    color: '#0F172A',
    minWidth: '160px'
  },

  searchGroup: {
    flex: 1,
    minWidth: '280px'
  },

  searchInputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },

  searchIcon: {
    position: 'absolute',
    left: '14px',
    fontSize: '18px',
    color: '#9CA3AF',
    pointerEvents: 'none'
  },

  searchInput: {
    width: '100%',
    padding: '10px 80px 10px 14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: "var(--font-sans)",
    background: '#FFFFFF',
    color: '#0F172A',
    transition: 'all 0.3s ease'
  },

  clearSearchBtn: {
    position: 'absolute',
    right: '12px',
    background: '#E5E7EB',
    border: '1px solid #CBD5E1',
    borderRadius: '999px',
    minWidth: '54px',
    height: '28px',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    color: '#334155',
    transition: 'all 0.2s ease'
  },

  resultsCount: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '12px',
    padding: '6px 12px',
    background: '#F9FAFB',
    borderRadius: '6px',
    display: 'inline-block',
    border: '1px solid #E5E7EB'
  },

  emptyState: {
    background: '#FFFFFF',
    borderRadius: '12px',
    padding: '40px 24px',
    textAlign: 'center',
    border: '2px dashed #D1D5DB'
  },

  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
    opacity: 0.5
  },

  emptyTitle: {
    fontSize: '20px',
    color: '#0F172A',
    marginBottom: '12px',
    fontFamily: "var(--font-sans)",
    fontWeight: '700'
  },

  emptyText: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '24px',
    maxWidth: '500px',
    margin: '0 auto 24px',
    lineHeight: '1.6'
  },

  clearSearchButton: {
    padding: '10px 24px',
    background: '#0F172A',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.3s ease'
  },

  tableWrapper: {
    background: '#FFFFFF',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },

  paginationWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '20px',
    flexWrap: 'wrap'
  },

  paginationBtn: {
    width: '40px',
    height: '40px',
    border: '1px solid #E2E8F0',
    background: '#FFFFFF',
    color: '#1E293B',
    fontSize: '14px',
    fontFamily: "var(--font-sans)",
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },

  paginationBtnActive: {
    background: 'linear-gradient(135deg, #1E293B, #334155)',
    color: '#FFFFFF',
    borderColor: '#1E293B',
    boxShadow: '0 12px 22px rgba(30, 41, 59, 0.28)',
    transform: 'translateY(-1px)'
  },

  paginationBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
    boxShadow: '0 6px 14px rgba(15, 23, 42, 0.05)'
  },

  paginationArrow: {
    fontSize: '24px',
    lineHeight: 1
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },

  th: {
    padding: '12px 10px',
    textAlign: 'left',
    background: '#F9FAFB',
    color: '#374151',
    fontWeight: '700',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: "var(--font-sans)",
    borderBottom: '2px solid #E5E7EB'
  },

  td: {
    padding: '10px',
    borderBottom: '1px solid #F3F4F6',
    color: '#0F172A',
    fontSize: '14px',
    fontFamily: "var(--font-sans)"
  },

  tableRow: {
    transition: 'background 0.2s ease',
    animation: 'fadeInUp 0.4s ease-out',
    cursor: 'pointer'
  },

  candidateCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#0F172A',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '13px',
    flexShrink: 0
  },

  candidateName: {
    fontWeight: '600',
    fontSize: '14px',
    color: '#0F172A'
  },

  candidateId: {
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '2px'
  },

  interviewCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  interviewMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#334155',
    fontSize: '13px',
    lineHeight: '1.5'
  },

  interviewStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    borderRadius: '6px',
    background: '#EFF6FF',
    color: '#1D4ED8',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'capitalize',
    fontFamily: "var(--font-sans)"
  },

  interviewStatusBadgeCancelled: {
    background: '#FEE2E2',
    color: '#B91C1C'
  },

  linkBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 14px',
    borderRadius: '6px',
    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
    color: '#FFFFFF',
    textDecoration: 'none',
    fontSize: '13px',
    fontWeight: '700',
    fontFamily: "var(--font-sans)",
    boxShadow: '0 6px 18px rgba(30,64,175,0.18)'
  },

  linkBtnCancelled: {
    background: 'linear-gradient(135deg, #B91C1C 0%, #EF4444 100%)',
    boxShadow: '0 6px 18px rgba(185,28,28,0.18)'
  },

  mutedText: {
    color: '#94A3B8',
    fontSize: '13px',
    fontFamily: "var(--font-sans)"
  },

  organizerText: {
    marginTop: '8px',
    color: '#475569',
    fontSize: '12px',
    fontFamily: "var(--font-sans)"
  },

  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize',
    fontFamily: "var(--font-sans)"
  },

  scheduleBtn: {
    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
    color: '#FFFFFF',
    padding: '7px 14px',
    border: 'none',
    borderRadius: '6px',
    fontWeight: '600',
    fontFamily: "var(--font-sans)",
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '13px',
    boxShadow: '0 6px 18px rgba(30,64,175,0.18)'
  },

  manageBtn: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    color: '#FFFFFF',
    border: '1px solid #0F172A',
    borderRadius: '8px',
    fontWeight: '700',
    fontFamily: "var(--font-sans)",
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '13px',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.18)',
    minWidth: '170px'
  },

  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    padding: '16px',
    animation: 'fadeIn 0.3s ease-out'
  },

  modal: {
    background: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    animation: 'slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
    border: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column'
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 20px',
    borderBottom: '1px solid #E5E7EB',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 10
  },

  modalTitle: {
    margin: 0,
    color: '#FFFFFF',
    fontSize: '20px',
    fontWeight: '700',
    fontFamily: "var(--font-sans)"
  },

  closeBtn: {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#FFFFFF',
    fontSize: '24px',
    cursor: 'pointer',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    transition: 'all 0.3s ease'
  },

  modalContent: {
    padding: '24px 20px',
    color: '#0F172A',
    overflowY: 'auto',
    flex: 1,
    animation: 'fadeIn 0.5s ease-out 0.1s both'
  },

  candidateInfo: {
    background: 'linear-gradient(135deg, #F0F4FF 0%, #F8FAFC 100%)',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '16px',
    border: '1px solid #E0E7FF',
    boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)'
  },

  infoLabel: {
    margin: '8px 0',
    fontSize: '14px',
    color: '#374151',
    fontFamily: "var(--font-sans)",
    lineHeight: '1.6'
  },

  formGroup: {
    marginBottom: '16px',
    animation: 'fadeIn 0.4s ease-out'
  },

  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#0F172A',
    fontFamily: "var(--font-sans)",
    letterSpacing: '0.3px'
  },

  input: {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: "var(--font-sans)",
    boxSizing: 'border-box',
    transition: 'all 0.3s ease',
    background: '#FFFFFF',
    color: '#0F172A'
  },

  helperText: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#475569',
    fontFamily: "var(--font-sans)"
  },

  modeSwitcher: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px'
  },

  modeButton: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    background: '#FFFFFF',
    color: '#0F172A',
    fontSize: '14px',
    fontWeight: '700',
    fontFamily: "var(--font-sans)",
    cursor: 'pointer',
    transition: 'all 0.25s ease'
  },

  modeButtonActive: {
    background: '#1E3A8A',
    color: '#FFFFFF',
    border: '1px solid #1E3A8A',
    boxShadow: '0 8px 20px rgba(30, 58, 138, 0.18)'
  },

  aiSection: {
    marginTop: '20px',
    padding: '16px',
    border: '1px solid #D6E4FF',
    borderRadius: '14px',
    background: 'linear-gradient(180deg, #F8FBFF 0%, #EEF4FF 100%)'
  },

  aiSectionHeader: {
    marginBottom: '18px',
    paddingBottom: '12px',
    borderBottom: '1px solid #D6E4FF'
  },

  aiSectionTitle: {
    margin: '0 0 6px 0',
    fontSize: '16px',
    color: '#1E3A8A',
    fontFamily: "var(--font-sans)"
  },

  aiSectionText: {
    margin: 0,
    fontSize: '13px',
    color: '#475569',
    lineHeight: '1.6',
    fontFamily: "var(--font-sans)"
  },

  generatedLinkCardFooter: {
    width: '100%',
    marginBottom: '10px',
    padding: '14px',
    border: '1px solid #BFDBFE',
    borderRadius: '10px',
    background: '#FFFFFF'
  },

  generatedLinkTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1E3A8A',
    marginBottom: '8px',
    fontFamily: "var(--font-sans)"
  },

  generatedLinkAnchor: {
    display: 'block',
    marginBottom: '10px',
    color: '#1D4ED8',
    fontSize: '13px',
    lineHeight: '1.5',
    wordBreak: 'break-all',
    textDecoration: 'underline',
    fontFamily: "var(--font-sans)"
  },

  copyLinkBtn: {
    background: '#DBEAFE',
    color: '#1E3A8A',
    border: '1px solid #93C5FD',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "var(--font-sans)"
  },

  modalFooter: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px 20px',
    borderTop: '1px solid #E5E7EB',
    background: '#F9FAFB',
    flexShrink: 0,
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    flexWrap: 'wrap'
  },

  modalFooterActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    width: '100%',
    flexWrap: 'wrap'
  },

  okBtn: {
    background: 'linear-gradient(135deg, #1E40AF 0%, #1e3a8a 100%)',
    color: '#FFFFFF',
    padding: '10px 24px',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
    minWidth: '140px'
  },

  cancelBtn: {
    background: '#E5E7EB',
    color: '#374151',
    padding: '10px 24px',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.3s ease',
    minWidth: '100px'
  },

  addBtn: {
    width: '100%',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    color: '#FFFFFF',
    padding: '12px 16px',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)'
  },

  addInterviewerRowBtn: {
    width: 'auto',
    minWidth: '150px',
    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
    color: '#FFFFFF',
    padding: '10px 16px',
    border: '1px solid #1E40AF',
    borderRadius: '8px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.25s ease',
    boxShadow: '0 6px 16px rgba(30, 64, 175, 0.18)'
  },

  removeInterviewerBtn: {
    flexShrink: 0,
    background: '#FFF1F2',
    color: '#B91C1C',
    padding: '10px 14px',
    border: '1.5px solid #FCA5A5',
    borderRadius: '8px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.25s ease',
    boxShadow: '0 4px 12px rgba(185, 28, 28, 0.08)'
  },

  deleteBtn: {
    background: 'transparent',
    color: '#DC2626',
    padding: '8px 14px',
    border: '1.5px solid rgba(220, 38, 38, 0.2)',
    borderRadius: '6px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '12px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap'
  },

  deleteBtnCancelled: {
    background: '#FEE2E2',
    color: '#B91C1C',
    border: '1.5px solid rgba(185, 28, 28, 0.25)',
    boxShadow: '0 4px 12px rgba(185, 28, 28, 0.08)'
  },

  editBtn: {
    background: 'transparent',
    color: '#2563EB',
    padding: '8px 14px',
    border: '1.5px solid rgba(37, 99, 235, 0.2)',
    borderRadius: '6px',
    fontWeight: '700',
    cursor: 'pointer',
    fontSize: '12px',
    fontFamily: "var(--font-sans)",
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap'
  },

  divider: {
    border: 'none',
    borderTop: '2px solid #E5E7EB',
    margin: '32px 0',
    background: 'transparent'
  },

  interviewersList: {
    marginTop: '24px'
  },

  sectionTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: '12px',
    fontFamily: "'Times new Roman', Times, serif",
    animation: 'fadeIn 0.4s ease-out'
  }
};
